/**
 * WebSocket 服务 - 用于实时接收盲盒统计更新
 */

// 开发环境: 前端运行在 3000 端口，后端在 5000 端口
// 生产环境: 前端和后端都在同一域名下
const getWsUrl = () => {
  if (process.env.REACT_APP_WS_URL) {
    return process.env.REACT_APP_WS_URL;
  }
  
  // 开发环境检测
  if (window.location.port === '3000') {
    // 开发环境，连接到后端的 5000 端口
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.hostname}:5000/ws`;
  }
  
  // 生产环境，使用当前域名
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/ws`;
};

const WS_URL = getWsUrl();

class WebSocketService {
  constructor() {
    this.ws = null;
    this.listeners = new Map(); // eventType -> Set<callback>
    this.subscribedRooms = new Set();
    this.reconnectTimer = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.reconnectDelay = 3000;
  }

  /**
   * 连接 WebSocket
   */
  connect() {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    console.log('正在连接 WebSocket:', WS_URL);
    
    try {
      this.ws = new WebSocket(WS_URL);

      this.ws.onopen = () => {
        console.log('WebSocket 已连接');
        this.reconnectAttempts = 0;
        
        // 重新订阅之前订阅的房间
        this.subscribedRooms.forEach(roomId => {
          this._sendSubscribe(roomId);
        });
        
        this._emit('connected', {});
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this._emit(data.type, data);
        } catch (error) {
          console.error('解析 WebSocket 消息失败:', error);
        }
      };

      this.ws.onclose = () => {
        console.log('WebSocket 已断开');
        this._emit('disconnected', {});
        this._scheduleReconnect();
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket 错误:', error);
      };
    } catch (error) {
      console.error('创建 WebSocket 连接失败:', error);
      this._scheduleReconnect();
    }
  }

  /**
   * 断开连接
   */
  disconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    
    this.subscribedRooms.clear();
  }

  /**
   * 订阅房间
   */
  subscribeRoom(roomId) {
    this.subscribedRooms.add(String(roomId));
    
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this._sendSubscribe(roomId);
    } else {
      // 如果未连接，先连接
      this.connect();
    }
  }

  /**
   * 取消订阅房间
   */
  unsubscribeRoom(roomId) {
    this.subscribedRooms.delete(String(roomId));
    
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({
        type: 'unsubscribe',
        roomId: String(roomId)
      }));
    }
  }

  /**
   * 认证用户身份（用于接收针对特定用户的消息）
   */
  authenticate(userId) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({
        type: 'auth',
        userId: userId
      }));
    }
  }

  /**
   * 添加事件监听器
   */
  on(eventType, callback) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType).add(callback);
    
    return () => {
      this.off(eventType, callback);
    };
  }

  /**
   * 移除事件监听器
   */
  off(eventType, callback) {
    const callbacks = this.listeners.get(eventType);
    if (callbacks) {
      callbacks.delete(callback);
    }
  }

  /**
   * 检查是否已连接
   */
  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * 发送订阅请求
   */
  _sendSubscribe(roomId) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({
        type: 'subscribe',
        roomId: String(roomId)
      }));
    }
  }

  /**
   * 触发事件
   */
  _emit(eventType, data) {
    const callbacks = this.listeners.get(eventType);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('事件回调执行失败:', error);
        }
      });
    }
  }

  /**
   * 计划重连
   */
  _scheduleReconnect() {
    if (this.reconnectTimer) {
      return;
    }
    
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('WebSocket 重连次数已达上限');
      return;
    }
    
    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(1.5, this.reconnectAttempts - 1);
    
    console.log(`WebSocket 将在 ${delay}ms 后重连 (第 ${this.reconnectAttempts} 次)`);
    
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }
}

// 单例模式
const websocketService = new WebSocketService();

export default websocketService;
